import React, { useMemo, useEffect, useState } from 'react';

type Props = {
  myId: string | null;
  card: string[] | null;
  marks: boolean[] | null;
  onMark: (index: number) => void;
  state: any;
  send: (msg: any) => void;
};

export default function BingoBoard({ myId, card, marks, onMark, state, send }: Props) {
  const [winAnimations, setWinAnimations] = useState<string[]>([]);
  const [lastWinPattern, setLastWinPattern] = useState<string | null>(null);

  const theme = state?.game?.themeSlug || 'wrestling';
  const allPlayers = state?.players || [];
  const allCards = state?.game?.cards || {};
  const allMarks = state?.game?.marks || {};

  // Check for win patterns in current marks
  const winPatterns = useMemo(() => {
    if (!marks || marks.length !== 25) return [];
    return checkAllWinPatterns(marks);
  }, [marks]);

  // Animate win patterns
  useEffect(() => {
    if (winPatterns.length > 0 && winPatterns[0] !== lastWinPattern) {
      setWinAnimations(winPatterns);
      setLastWinPattern(winPatterns[0]);
      
      // Clear animation after 3 seconds
      setTimeout(() => {
        setWinAnimations([]);
      }, 3000);
    }
  }, [winPatterns, lastWinPattern]);

  const getThemeConfig = (themeSlug: string) => {
    const configs = {
      wrestling: {
        emoji: '🤼',
        gradient: 'from-red-500 to-orange-500',
        name: 'Wrestling',
        color: 'red'
      },
      'rap-battle': {
        emoji: '🎤', 
        gradient: 'from-purple-500 to-pink-500',
        name: 'Rap Battle',
        color: 'purple'
      },
      bodycam: {
        emoji: '📹',
        gradient: 'from-blue-500 to-cyan-500', 
        name: 'Bodycam',
        color: 'blue'
      }
    };
    return configs[themeSlug as keyof typeof configs] || configs.wrestling;
  };

  const themeConfig = getThemeConfig(theme);
  const markedCount = marks?.filter(Boolean).length || 0;
  const totalCells = 25;
  const progress = (markedCount / totalCells) * 100;

  // Get win line indices for animation
  const getWinLineIndices = (pattern: string): number[] => {
    if (pattern.startsWith('row-')) {
      const row = parseInt(pattern.split('-')[1]);
      return Array.from({length: 5}, (_, i) => row * 5 + i);
    }
    if (pattern.startsWith('col-')) {
      const col = parseInt(pattern.split('-')[1]);
      return Array.from({length: 5}, (_, i) => i * 5 + col);
    }
    if (pattern === 'diag-main') {
      return [0, 6, 12, 18, 24];
    }
    if (pattern === 'diag-anti') {
      return [4, 8, 12, 16, 20];
    }
    return [];
  };

  const isWinningCell = (index: number): boolean => {
    return winAnimations.some(pattern => 
      getWinLineIndices(pattern).includes(index)
    );
  };

  const getPlayerStats = () => {
    return allPlayers.map(player => {
      const playerMarks = allMarks[player.id] || [];
      const playerCard = allCards[player.id] || [];
      const playerMarkedCount = playerMarks.filter(Boolean).length;
      const playerWins = checkAllWinPatterns(playerMarks);
      
      return {
        ...player,
        markedCount: playerMarkedCount,
        hasWin: playerWins.length > 0,
        winPatterns: playerWins
      };
    }).sort((a, b) => {
      // Sort by: wins first, then by marked count
      if (a.hasWin && !b.hasWin) return -1;
      if (!a.hasWin && b.hasWin) return 1;
      return b.markedCount - a.markedCount;
    });
  };

  const playerStats = getPlayerStats();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center space-x-3">
          <div className={`w-14 h-14 bg-gradient-to-br ${themeConfig.gradient} rounded-2xl flex items-center justify-center text-3xl shadow-xl`}>
            {themeConfig.emoji}
          </div>
          <div>
            <h2 className="text-3xl font-bold text-white">Bingo Board</h2>
            <p className="text-slate-400">{themeConfig.name} Theme</p>
          </div>
        </div>
        
        {card && marks && (
          <div className="text-right">
            <div className="text-sm text-slate-400 mb-2">Your Progress</div>
            <div className="flex items-center space-x-3">
              <div className="w-24 h-3 bg-slate-700 rounded-full overflow-hidden">
                <div 
                  className={`h-full bg-gradient-to-r ${themeConfig.gradient} transition-all duration-700 ease-out`}
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="text-lg font-bold text-white">{markedCount}/25</span>
            </div>
            {winPatterns.length > 0 && (
              <div className="text-green-400 font-bold text-sm mt-1 animate-pulse">
                🎉 BINGO! ({winPatterns.join(', ')})
              </div>
            )}
          </div>
        )}
      </div>

      {/* Player Leaderboard */}
      {allPlayers.length > 1 && (
        <div className="glass rounded-xl p-4">
          <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            🏆 Player Stats
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {playerStats.map((player, index) => (
              <div 
                key={player.id}
                className={`p-3 rounded-lg border transition-all ${
                  player.id === myId 
                    ? `border-${themeConfig.color}-500/60 bg-${themeConfig.color}-500/10` 
                    : 'border-slate-600/50 bg-slate-800/40'
                } ${player.hasWin ? 'ring-2 ring-yellow-400/50' : ''}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${themeConfig.gradient} flex items-center justify-center text-white text-sm font-bold`}>
                      {player.nick.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="font-medium text-white">
                        {player.nick}
                        {player.id === myId && (
                          <span className="text-xs text-slate-400 ml-1">(You)</span>
                        )}
                      </div>
                      <div className="text-xs text-slate-400">
                        {player.markedCount}/25 marked
                      </div>
                    </div>
                  </div>
                  {player.hasWin && (
                    <div className="text-yellow-400 text-lg">👑</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bingo Grid */}
      {card && marks ? (
        <div className="glass rounded-2xl p-6">
          <div className="grid grid-cols-5 gap-2 sm:gap-3 max-w-2xl mx-auto">
            {card.map((cell: string, i: number) => {
              const isMarked = marks[i];
              const isCenter = i === 12; // Free space in 5x5 grid
              const isWinning = isWinningCell(i);
              
              return (
                <button 
                  key={i}
                  onClick={() => onMark(i)}
                  disabled={isCenter && !cell} // Disable free space if empty
                  className={`
                    relative h-16 sm:h-20 text-xs font-medium px-2 py-2 rounded-xl border-2 transition-all duration-300 transform hover:scale-105 active:scale-95
                    ${isMarked 
                      ? `bg-gradient-to-br ${themeConfig.gradient} border-transparent text-white shadow-lg` 
                      : 'bg-slate-800/60 border-slate-700/50 text-slate-300 hover:bg-slate-700/70 hover:border-slate-600'
                    }
                    ${isCenter ? 'ring-2 ring-yellow-500/50' : ''}
                    ${isWinning ? 'animate-pulse ring-4 ring-yellow-400' : ''}
                    disabled:cursor-default
                  `}
                >
                  {/* Cell content */}
                  <div className="relative z-10 h-full flex items-center justify-center text-center leading-tight">
                    {isCenter && !cell ? (
                      <div className="text-yellow-400 font-bold text-sm">FREE</div>
                    ) : (
                      <span className="break-words">{cell}</span>
                    )}
                  </div>
                  
                  {/* Marked indicator */}
                  {isMarked && (
                    <div className="absolute top-1 right-1 w-4 h-4 bg-white rounded-full flex items-center justify-center shadow-lg">
                      <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}

                  {/* Winning animation overlay */}
                  {isWinning && (
                    <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/30 to-orange-400/30 rounded-xl animate-pulse" />
                  )}

                  {/* Hover effect */}
                  <div className="absolute inset-0 bg-white/5 rounded-xl opacity-0 hover:opacity-100 transition-opacity duration-200" />
                </button>
              );
            })}
          </div>

          {/* Win patterns guide */}
          <div className="mt-6 p-4 bg-slate-800/30 rounded-xl border border-slate-700/30">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 text-slate-400 text-sm">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Get 5 in a row to win!</span>
              </div>
              <div className="text-xs">
                Valid patterns: Horizontal, Vertical, or Diagonal lines
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="glass rounded-2xl p-6">
          <div className="flex flex-col items-center justify-center py-16 text-slate-400">
            <div className="w-20 h-20 border-4 border-slate-600 border-t-purple-500 rounded-full animate-spin mb-6"></div>
            <p className="text-xl font-medium mb-2">Generating your bingo card...</p>
            <p className="text-sm">This may take a moment</p>
            {!myId && (
              <p className="text-xs mt-2 text-red-400">Please make sure you're connected and have joined the room</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Helper function to check all possible win patterns
function checkAllWinPatterns(marks: boolean[]): string[] {
  if (!marks || marks.length !== 25) return [];
  
  const patterns: string[] = [];
  const size = 5;

  // Check rows
  for (let r = 0; r < size; r++) {
    const rowStart = r * size;
    if (Array.from({length: size}, (_, i) => marks[rowStart + i]).every(Boolean)) {
      patterns.push(`row-${r}`);
    }
  }

  // Check columns  
  for (let c = 0; c < size; c++) {
    if (Array.from({length: size}, (_, i) => marks[i * size + c]).every(Boolean)) {
      patterns.push(`col-${c}`);
    }
  }

  // Check main diagonal (top-left to bottom-right)
  if ([0, 6, 12, 18, 24].every(i => marks[i])) {
    patterns.push('diag-main');
  }

  // Check anti-diagonal (top-right to bottom-left)
  if ([4, 8, 12, 16, 20].every(i => marks[i])) {
    patterns.push('diag-anti');
  }

  return patterns;
}